import type { MeResponse } from '@/lib/api/auth';
import type { SnappyAdmission, SnappyUser } from '@/lib/api/snappy';

import type { FlowAvailability, FlowFlagsEntry } from './types';

export interface AvailabilityInput {
    me: MeResponse | null;
    /** `GET /v2/snappy/user`, or null when it failed / was not asked. */
    snappyUser: Pick<SnappyUser, 'backups'> | null;
    /** `GET /factions/speed/snappy/admission`, or null when it failed. */
    admission: SnappyAdmission | null;
    flags: FlowFlagsEntry | undefined;
    /** The key held in this browser, if any. */
    heldAddress?: string | null;
}

/** Wallet-first and passkey users: only the flow that has always existed. */
export const NO_SOCIAL_AVAILABILITY: FlowAvailability = Object.freeze({
    v1: true,
    v2: false,
    v1Address: null,
    v2Address: null,
});

/** The custodial wallet the V1 flow trades from, or the anchor wallet. */
export function v1AddressOf(me: MeResponse | null): string | null {
    if (!me?.authenticated) return null;
    return (
        me.custodialWalletAddress ??
        me.anchors?.['25'] ??
        me.anchors?.['4663'] ??
        null
    );
}

/**
 * Which flows an account may use (plan §3.2, §4.2). Pure.
 *
 * V1: bound, or a custodial / anchor wallet, or a validated non-V2 code.
 * V2: an admission row while the launch switch is ON, or a backup slot (the
 * mobile and WolfSwap users, owner rule 7), or a V2 code redeemed from this
 * browser. With the switch OFF the admission route answers `admitted: true`
 * for everyone, so admission alone never unlocks V2 then: every V1 user would
 * see the toggle.
 */
export function computeAvailability({
    me,
    snappyUser,
    admission,
    flags,
    heldAddress = null,
}: AvailabilityInput): FlowAvailability {
    if (!me?.authenticated || !me.accountHash) return NO_SOCIAL_AVAILABILITY;

    const v1Address = v1AddressOf(me);
    const v1 =
        me.bindingState === 'bound' ||
        v1Address !== null ||
        flags?.v1 !== undefined;

    const backups = snappyUser?.backups ?? [];
    const held = heldAddress?.toLowerCase() ?? null;
    const heldIsListed =
        held !== null && backups.some((b) => b.address.toLowerCase() === held);
    const v2Address = heldIsListed
        ? heldAddress
        : (backups[0]?.address ?? null);
    const v2 =
        (admission?.enabled === true && admission.admitted === true) ||
        backups.length > 0 ||
        flags?.v2 !== undefined;

    return { v1, v2, v1Address, v2Address };
}
